import { useEffect, useRef, useState } from "react";

import { Circle, CircleCheck, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { useMutation } from "@redwoodjs/web";

import { Button } from "src/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "src/components/ui/command";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from "src/components/ui/responsive-dialog";
import { ScrollArea, ScrollBar } from "src/components/ui/scroll-area";
import { Separator } from "src/components/ui/separator";
import { allIntegrationTypes, storeIntegrationTypes } from "src/lib/constants";
import { useIntegration } from "src/lib/hooks/useIntegration";
import { INTEGRATION_URL_MUTATION } from "src/lib/queries/integrations";

import { MetaConnectionDialog } from "./MetaConnectionDialog";

export const ManageModal = ({
  integration,
  open,
  setOpen,
  descriptionRender,
  multiselect,
  refetch,
}) => {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const saveInProgress = useRef(false);
  const needsReconnect =
    integration?.activeIssue?.quickFixType === "RECONNECT" &&
    !integration?.credentialStatus?.awaitingSync;
  const [metaConnectOpen, setMetaConnectOpen] = useState(false);
  useEffect(() => {
    setMetaConnectOpen(false);
    setSaveError(null);
  }, [integration?.id, open]);
  const {
    sources: allSources,
    querySources,
    updateIntegration,
    deleteIntegration,
    loading,
  } = useIntegration(allIntegrationTypes[integration?.type?.toLowerCase()], {
    queryOnRender: false,
  });
  const { refetch: refetchStore } = useIntegration(
    storeIntegrationTypes.shopify,
    {
      queryOnRender: false,
    }
  );
  const [createIntegrationUrl, { loading: reconnectLoading }] = useMutation(
    INTEGRATION_URL_MUTATION,
    {
      onCompleted: (data) => {
        window.location.assign(data.integrationUrl);
      },
      onError: (error) => {
        toast.error(error.message, {
          description: "Please try again",
        });
      },
    }
  );

  const sources = allSources[integration?.id];
  const [updatedSources, setUpdatedSources] = useState(sources);
  const busy = loading || saving;
  const dialogDescription = saving
    ? "Saving sources..."
    : loading
      ? "Loading..."
      : descriptionRender?.(updatedSources);

  useEffect(() => {
    setUpdatedSources(sources);
  }, [sources]);

  useEffect(() => {
    if (open && integration && !needsReconnect) {
      querySources(integration);
    }
    // eslint-disable-next-line
  }, [open, integration, needsReconnect]);

  if (!integration) {
    return null;
  }

  const onSave = async () => {
    if (saveInProgress.current || loading || !updatedSources) return;
    saveInProgress.current = true;
    setSaving(true);
    setSaveError(null);

    const sources = updatedSources
      ?.filter(({ enabled }) => enabled)
      ?.map(({ id, name, currency }) => ({
        id,
        name,
        currency,
      }));

    try {
      const result = await updateIntegration({
        id: integration.id,
        input: {
          settings: {
            sources,
          },
        },
      });

      if (
        result.errors?.length ||
        result.error ||
        !result.data?.updateIntegration
      ) {
        setSaveError("Could not save sources. Please try again.");
        return;
      }

      // The save is complete. Refresh the card after closing, without an idle
      // Save button or another source selection while the refresh is pending.
      setOpen(false);
      await (refetch || refetchStore)();
    } catch {
      setSaveError("Could not save sources. Please try again.");
    } finally {
      saveInProgress.current = false;
      setSaving(false);
    }
  };

  const onReconnect = async () => {
    if (integration.type === "META_ADS") {
      setMetaConnectOpen(true);
      return;
    }
    await createIntegrationUrl({
      variables: {
        input: {
          type: integration.type,
          parentId: integration.parentId,
        },
      },
    });
  };

  return (
    <>
      <ResponsiveDialog
        open={open && !metaConnectOpen}
        onOpenChange={(nextOpen) => {
          if (!saveInProgress.current) setOpen(nextOpen);
        }}
      >
        <ResponsiveDialogContent>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle>
              {integration.description}
            </ResponsiveDialogTitle>
            {!needsReconnect && (
              <ResponsiveDialogDescription>
                {dialogDescription}
              </ResponsiveDialogDescription>
            )}
          </ResponsiveDialogHeader>

          {integration.type === "META_ADS" && !needsReconnect && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/20 p-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">Meta connection</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {integration.credentialStatus?.awaitingSync
                    ? "Connection verified. Your data is catching up."
                    : integration.credentialStatus?.expiresSoon
                      ? "Access expires soon. Reconnect to keep data up to date."
                      : "Update access or change how you connect to Meta."}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={onReconnect}
                disabled={busy}
              >
                <RefreshCw className="size-3.5" />
                Reconnect
              </Button>
            </div>
          )}

          {needsReconnect ? (
            <div className="border-destructive/30 bg-destructive/5 rounded-lg border p-4">
              <div className="text-destructive text-sm font-medium">
                {integration.activeIssue?.title || "Reconnect required"}
              </div>
              <div className="text-muted-foreground mt-1 text-sm">
                {integration.activeIssue?.description ||
                  "Reconnect this integration before updating sources."}
              </div>
            </div>
          ) : (
            <Command className="rounded-lg border overflow-visible">
              <CommandInput placeholder="Search sources..." disabled={busy} />

              <ScrollArea
                className="max-h-72 pr-3 -mr-3 flex flex-col"
                type="always"
              >
                <CommandList className="max-h-none">
                  <CommandEmpty>
                    {loading ? (
                      <Loader2 className="animate-spin mx-auto" />
                    ) : (
                      "No sources found."
                    )}
                  </CommandEmpty>

                  {!!updatedSources?.length &&
                    Object.entries(
                      updatedSources?.reduce((acc, source) => {
                        const parent = source.parentName || "Uncategorized";
                        if (!acc[parent]) {
                          acc[parent] = [];
                        }
                        acc[parent].push(source);
                        return acc;
                      }, {})
                    ).map(([parent, items]) => (
                      <div key={parent}>
                        <CommandSeparator />
                        <CommandGroup heading={parent}>
                          {items.map((source) => {
                            return (
                              <CommandItem
                                key={source.id}
                                value={source.name}
                                disabled={busy}
                                className={source.enabled ? "bg-accent/60" : ""}
                                onSelect={() => {
                                  if (multiselect) {
                                    setUpdatedSources((prev) =>
                                      prev.map((s) => {
                                        if (s.id === source.id) {
                                          return { ...s, enabled: !s.enabled };
                                        }
                                        return s;
                                      })
                                    );
                                  } else {
                                    setUpdatedSources((prev) =>
                                      prev.map((s) => ({
                                        ...s,
                                        enabled:
                                          s.id === source.id
                                            ? !s.enabled
                                            : false,
                                      }))
                                    );
                                  }
                                }}
                              >
                                {source.name}
                                <CommandShortcut>
                                  {source.enabled ? (
                                    <CircleCheck className="text-primary" />
                                  ) : (
                                    <Circle />
                                  )}
                                </CommandShortcut>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </div>
                    ))}
                </CommandList>

                <ScrollBar />
              </ScrollArea>
            </Command>
          )}

          {saveError && (
            <p role="alert" className="text-destructive text-sm">
              {saveError}
            </p>
          )}

          <ResponsiveDialogFooter>
            <Button
              variant="destructive"
              onClick={() => setDeleteOpen(true)}
              disabled={busy}
              className="mt-2 sm:mt-0 sm:mr-auto"
            >
              Disconnect
            </Button>
            <Separator className="mt-2 sm:hidden" />

            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={busy}
            >
              Cancel
            </Button>

            {needsReconnect ? (
              <Button onClick={onReconnect} disabled={reconnectLoading}>
                {reconnectLoading && (
                  <Loader2 className="size-4 animate-spin" />
                )}
                Reconnect
              </Button>
            ) : (
              <Button onClick={onSave} disabled={busy || !updatedSources}>
                {busy && <Loader2 className="size-4 animate-spin" />}
                Save
              </Button>
            )}
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>

      <MetaConnectionDialog
        open={open && metaConnectOpen}
        onOpenChange={setMetaConnectOpen}
        parentId={integration.parentId}
        reconnectIntegrationId={integration.id}
      />

      <ResponsiveDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <ResponsiveDialogContent>
          <ResponsiveDialogTitle>
            Disconnect {integration.description}
          </ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            Are you sure you want to disconnect this integration? This will
            remove all data associated with it.
          </ResponsiveDialogDescription>
          <ResponsiveDialogFooter>
            <Button variant="ghost" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                const { errors } = await deleteIntegration({
                  id: integration.id,
                });

                if (!errors) {
                  await refetchStore();
                  setDeleteOpen(false);
                  setOpen(false);
                }
              }}
              disabled={loading}
            >
              {loading && <Loader2 className="size-4 animate-spin" />}
              Disconnect this integration
            </Button>
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>
    </>
  );
};
